import { Flex } from '@chakra-ui/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Provider as JotaiProvider } from 'jotai'
import { useEffect, useState } from 'react'
import { I18nextProvider, useTranslation } from 'react-i18next'
import { RouterProvider } from 'react-router/dom'
import { useSettingsState } from '~/atoms/SettingsState'
import { ConnectionStatusListener } from '~/components/ConnectionStatusListener'
import { LiveAnnouncer } from '~/components/shared/LiveAnnouncer'
import { LoadingSpinner } from '~/components/shared/LoadingSpinner'
import { ElectronThemeSync } from '~/components/specialized/ElectronThemeSync'
import { RequiredUpdateGate } from '~/components/specialized/RequiredUpdate/RequiredUpdateGate'
import { SupportChatWidget } from '~/components/specialized/SupportChatWidget'
import { Provider } from '~/components/ui/provider'
import { useAgentModeSync } from '~/hooks/useAgentModeSync'
import { useAppHotkeys } from '~/hooks/useAppHotkeys'
import { usePartnerConfig } from '~/hooks/useCurrentPartner'
import { useNetworkConnectivity } from '~/hooks/useNetworkConnectivity'
import { CallsExportProvider } from '~/providers/CallsExportProvider'
import { ElectronUpdaterProvider } from '~/providers/ElectronUpdaterContext'
import { Sentry } from '~/services/sentry'
import { Size } from '~/types/ThemeTypes'
import { validateStartupAuth } from '~/utils/authStartupGuard'
import { isProductionBuildEnvironment } from '~/utils/common'

import { ModalLabelsProvider } from '@mangovoice/mango-ui'

import { i18nConfig } from './i18n.config'
import { queryClient } from './queryClient'
import { router } from './router'

function App() {
  const [authValidated, setAuthValidated] = useState(false)

  // Drive online/offline → connectionStatusAtom for the connection indicator.
  // ActiveCallsBlock plays the connection sounds during a call.
  useNetworkConnectivity({ playConnectionChangeSound: false })

  // Set up app-level hotkeys that work throughout the app
  useAppHotkeys()

  // Sync Agent Mode setting with PBX capabilities
  useAgentModeSync()

  const { i18n, t } = useTranslation()
  const { settings } = useSettingsState()
  const partnerConfig = usePartnerConfig()

  useEffect(() => {
    // Use stored preference if available, otherwise auto-detect from system
    const languageToUse =
      settings?.language?.preferredLanguage ?? window.navigator.language
    void i18n.changeLanguage(languageToUse)
  }, [settings?.language?.preferredLanguage, i18n])

  // Validate authentication on startup to prevent service initialization loops
  useEffect(() => {
    validateStartupAuth()
    setAuthValidated(true)
  }, [])

  return (
    <Provider system={partnerConfig.theme}>
      <ModalLabelsProvider closeLabel={t('voicemail.tooltip.close')}>
        <ElectronThemeSync />
        <LiveAnnouncer />
        {!authValidated ? (
          <Flex height="100vh" align="center" justify="center">
            <LoadingSpinner size={Size.lg} />
          </Flex>
        ) : (
          <>
            <ConnectionStatusListener />
            <RequiredUpdateGate>
              <RouterProvider router={router} />
            </RequiredUpdateGate>
            <SupportChatWidget />
            {!isProductionBuildEnvironment && (
              <ReactQueryDevtools initialIsOpen={false} />
            )}
          </>
        )}
      </ModalLabelsProvider>
    </Provider>
  )
}

export default function AppWrapper() {
  return (
    <Sentry.ErrorBoundary>
      <JotaiProvider>
        <I18nextProvider i18n={i18nConfig} defaultNS={'translation'}>
          <QueryClientProvider client={queryClient}>
            <ElectronUpdaterProvider>
              <CallsExportProvider>
                <App />
              </CallsExportProvider>
            </ElectronUpdaterProvider>
          </QueryClientProvider>
        </I18nextProvider>
      </JotaiProvider>
    </Sentry.ErrorBoundary>
  )
}
